/**
 * AddDeliveryModal - Modal para adicionar/editar entrega
 * Permite buscar endereco e definir detalhes da entrega
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  MyLocation as LocationIcon,
  AccessTime as TimeIcon,
  Scale as WeightIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { searchAddress } from '../../common/services/routingService';
import PremiumInput from '../../common/components/premium/PremiumInput';

const AddDeliveryModal = ({
  open,
  onClose,
  onSave,
  initialData = null,
}) => {
  const tokens = usePremiumTokens();

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    lat: null,
    lng: null,
    weight: '',
    serviceTime: 5,
    priority: 'medium',
    timeWindowStart: '',
    timeWindowEnd: '',
    notes: '',
  });

  // Estados de busca (sem searchQuery separado - usa formData.address)
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Carregar dados iniciais se editando
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        address: initialData.address || '',
        lat: initialData.lat,
        lng: initialData.lng,
        weight: initialData.weight || '',
        serviceTime: initialData.serviceTime || 5,
        priority: initialData.priority || 'medium',
        timeWindowStart: initialData.timeWindow?.start || '',
        timeWindowEnd: initialData.timeWindow?.end || '',
        notes: initialData.notes || '',
      });
    } else {
      // Reset form
      setFormData({
        name: '',
        address: '',
        lat: null,
        lng: null,
        weight: '',
        serviceTime: 5,
        priority: 'medium',
        timeWindowStart: '',
        timeWindowEnd: '',
        notes: '',
      });
    }
    setSearchResults([]);
    setShowResults(false);
  }, [initialData, open]);

  // Handler para digitar no campo de endereco
  const handleAddressChange = useCallback((e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      address: value,
      // Limpar coordenadas quando usuario digita (endereco nao confirmado)
      lat: null,
      lng: null,
    }));
    setShowResults(false);
  }, []);

  // Buscar endereco
  const handleSearch = useCallback(async () => {
    if (formData.address.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const results = await searchAddress(formData.address, { limit: 5 });
      setSearchResults(results);
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [formData.address]);

  // Selecionar resultado da busca
  const handleSelectResult = (result) => {
    setFormData((prev) => ({
      ...prev,
      address: result.displayName,
      lat: result.lat,
      lng: result.lng,
      name: prev.name || result.address?.road || result.address?.suburb || '',
    }));
    setShowResults(false);
  };

  // Salvar
  const handleSave = () => {
    if (!formData.lat || !formData.lng) {
      return;
    }

    const delivery = {
      id: initialData?.id || `delivery-${Date.now()}`,
      name: formData.name,
      address: formData.address,
      lat: formData.lat,
      lng: formData.lng,
      weight: formData.weight ? Number(formData.weight) : undefined,
      serviceTime: formData.serviceTime * 60, // converter para segundos
      priority: formData.priority,
      timeWindow: formData.timeWindowStart && formData.timeWindowEnd
        ? { start: formData.timeWindowStart, end: formData.timeWindowEnd }
        : undefined,
      notes: formData.notes,
    };

    onSave(delivery);
    onClose();
  };

  const isValid = formData.lat && formData.lng;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      PaperProps={{
        sx: {
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.xl,
          border: `1px solid ${tokens?.border?.subtle}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          py: 1.5,
          px: 2,
        }}
      >
        <Typography sx={{ fontWeight: 600, color: tokens?.text?.primary }}>
          {initialData ? 'Editar Entrega' : 'Adicionar Entrega'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ color: tokens?.text?.secondary }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Busca de endereco */}
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              placeholder="Buscar endereco..."
              value={formData.address}
              onChange={handleAddressChange}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: tokens?.text?.tertiary }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searching ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Button size="small" onClick={handleSearch}>
                        Buscar
                      </Button>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: tokens?.surface?.elevated,
                  borderRadius: tokens?.radius?.md,
                },
              }}
            />

            {/* Resultados da busca */}
            {showResults && searchResults.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  mt: 0.5,
                  backgroundColor: tokens?.surface?.elevated,
                  borderRadius: tokens?.radius?.md,
                  border: `1px solid ${tokens?.border?.default}`,
                  boxShadow: tokens?.shadows?.lg,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <List dense disablePadding>
                  {searchResults.map((result) => (
                    <ListItemButton
                      key={result.id}
                      onClick={() => handleSelectResult(result)}
                      sx={{
                        py: 1,
                        '&:hover': {
                          backgroundColor: tokens?.surface?.hover,
                        },
                      }}
                    >
                      <ListItemText
                        primary={result.displayName}
                        primaryTypographyProps={{
                          sx: {
                            fontSize: '13px',
                            color: tokens?.text?.primary,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
          </Box>

          {/* Coordenadas selecionadas */}
          {formData.lat && formData.lng && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                backgroundColor: `${tokens?.accent?.primary}10`,
                borderRadius: tokens?.radius?.md,
                border: `1px solid ${tokens?.accent?.primary}30`,
              }}
            >
              <LocationIcon sx={{ color: tokens?.accent?.primary, fontSize: 20 }} />
              <Typography sx={{ color: tokens?.text?.primary, fontSize: '13px' }}>
                {formData.lat.toFixed(6)}, {formData.lng.toFixed(6)}
              </Typography>
              <Chip
                label="Localizado"
                size="small"
                sx={{
                  ml: 'auto',
                  backgroundColor: `${tokens?.semantic?.success}20`,
                  color: tokens?.semantic?.success,
                  height: 22,
                  fontSize: '11px',
                }}
              />
            </Box>
          )}

          <Divider sx={{ borderColor: tokens?.border?.subtle }} />

          {/* Nome da entrega */}
          <PremiumInput
            label="Nome/Referencia"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Ex: Cliente ABC, Loja Centro..."
          />

          <Grid container spacing={2}>
            {/* Peso */}
            <Grid size={6}>
              <PremiumInput
                label="Peso (kg)"
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData((prev) => ({ ...prev, weight: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WeightIcon sx={{ color: tokens?.text?.tertiary, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Tempo de servico */}
            <Grid size={6}>
              <PremiumInput
                label="Tempo no local (min)"
                type="number"
                value={formData.serviceTime}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  serviceTime: Number(e.target.value),
                }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TimeIcon sx={{ color: tokens?.text?.tertiary, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* Prioridade */}
          <FormControl fullWidth size="small">
            <InputLabel>Prioridade</InputLabel>
            <Select
              value={formData.priority}
              onChange={(e) => setFormData((prev) => ({ ...prev, priority: e.target.value }))}
              label="Prioridade"
              sx={{
                backgroundColor: tokens?.surface?.elevated,
                borderRadius: tokens?.radius?.md,
              }}
            >
              <MenuItem value="low">Baixa</MenuItem>
              <MenuItem value="medium">Normal</MenuItem>
              <MenuItem value="high">Alta (Prioritario)</MenuItem>
            </Select>
          </FormControl>

          {/* Janela de tempo */}
          <Grid container spacing={2}>
            <Grid size={6}>
              <PremiumInput
                label="Horario minimo"
                type="time"
                value={formData.timeWindowStart}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  timeWindowStart: e.target.value,
                }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={6}>
              <PremiumInput
                label="Horario maximo"
                type="time"
                value={formData.timeWindowEnd}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  timeWindowEnd: e.target.value,
                }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Observacoes */}
          <PremiumInput
            label="Observacoes"
            multiline
            rows={2}
            value={formData.notes}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Instrucoes especiais, contato, etc..."
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${tokens?.border?.subtle}`,
          p: 2,
          gap: 1,
        }}
      >
        <Button onClick={onClose} sx={{ color: tokens?.text?.secondary }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid}
          sx={{
            backgroundColor: tokens?.accent?.primary,
            '&:hover': {
              backgroundColor: tokens?.accent?.hover,
            },
            '&:disabled': {
              backgroundColor: tokens?.surface?.elevated,
            },
          }}
        >
          {initialData ? 'Salvar' : 'Adicionar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDeliveryModal;
